import { useEffect, useMemo, useState } from "react";
import { Table, Button, Select, Input, Icon, Pagination, PickListDropdown } from "common-components";
import { Breadcrumb } from "components";
import { Checkbox, DatePicker, Image, Spin } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import moment from "moment";
import openNotificationWithIcon from "utils/hooks/useNotification";
import { setSelectOptions } from "utils/common";
import {
	getErrorReportSelection,
	getErrorReportList,
	updateErrorReportsStatus,
	updateErrorReportsRemark,
	deprecateErrorReportQuestions,
	sendErrorReportMails,
	exportErrorReports,
} from "api/errorReport";
import { QuestionPreviewModal } from "./QuestionPreviewModal";
import { UpdateStatusModal } from "./UpdateStatusModal";
import { DeprecateQuestionModal } from "./DeprecateQuestionModal";
import { SendMailModal } from "./SendMailModal";
import { UpdateRemarkModal } from "./UpdateRemarkModal";
import { StyledErrorReportPage, UiPopupContainer } from "./ErrorReportPage.style";

const { RangePicker } = DatePicker;

const breadcrumbList = [
	{
		icon: "find_in_page",
		name: "錯題回報查詢",
		path: "/errorReport",
	},
];

/** 錯題回報 API 以 error 欄位回傳狀態碼 */
const API_SUCCESS_CODE = 200;

const PAGE_SIZE = 25;

const DATE_FORMAT = "YYYY-MM-DD";

const MAIL_ICON_COLOR = {
	blue: "#82b4ff",
	red: "#ff4d4f",
	green: "#4caf50",
};

const MAIL_STATUS = {
	NO_EMAIL: "NO_EMAIL", // 回報者沒留 EMAIL
	SENT: "SENT", // 已完成且已寄信
	SENDABLE: "SENDABLE", // 已完成、有 EMAIL 且尚未寄信
	UNAVAILABLE: "UNAVAILABLE", // 其餘不可寄信的情況
};

const MAIL_STATUS_COLOR = {
	[MAIL_STATUS.NO_EMAIL]: MAIL_ICON_COLOR.red,
	[MAIL_STATUS.SENT]: MAIL_ICON_COLOR.green,
	[MAIL_STATUS.SENDABLE]: MAIL_ICON_COLOR.blue,
	[MAIL_STATUS.UNAVAILABLE]: MAIL_ICON_COLOR.blue,
};

const getMailStatus = (report) => {
	const isStatusFinished = report.status?.code === "Finish";

	if (!report.contactEmail) return MAIL_STATUS.NO_EMAIL;
	if (isStatusFinished && report.isMailSent) return MAIL_STATUS.SENT;
	if (isStatusFinished && !report.isMailSent) return MAIL_STATUS.SENDABLE;
	return MAIL_STATUS.UNAVAILABLE;
};

/** 回報者資訊各欄位的顯示名稱，key 對應後端 reporterInfo 的欄位 */
const REPORTER_FIELD = {
	seatNo: { key: "seatNo", label: "座號" },
	userName: { key: "userName", label: "姓名" },
	teacherName: { key: "examCreatorName", label: "教師" },
	schoolName: { key: "schoolName", label: "學校" },
	examCreatorName: { key: "examCreatorName", label: "建卷者姓名" },
	page: { key: "page", label: "頁面" },
};

/** NaniExam 依回報頁面（NaniExam 前端寫死的中文字串）決定要顯示的欄位與順序 */
const EXAM_PAGE_FIELDS = {
	測驗: [REPORTER_FIELD.page, REPORTER_FIELD.seatNo, REPORTER_FIELD.userName],
	成績單: [REPORTER_FIELD.page, REPORTER_FIELD.seatNo, REPORTER_FIELD.userName],
	教師檢討: [REPORTER_FIELD.page, REPORTER_FIELD.teacherName, REPORTER_FIELD.schoolName],
	預覽試卷: [REPORTER_FIELD.page, REPORTER_FIELD.examCreatorName, REPORTER_FIELD.schoolName],
};

const getReporterFields = (report) => {
	switch (report.comeFrom?.code) {
		case "NaniPaper":
			return [REPORTER_FIELD.schoolName, REPORTER_FIELD.userName];
		case "NaniExam":
			// 不在清單內的頁面至少顯示頁面名稱
			return EXAM_PAGE_FIELDS[report.reporterInfo?.page] || [REPORTER_FIELD.page];
		default:
			// 內部檢查（QuestionBank）不顯示回報者資訊
			return [];
	}
};

/** 後端沒給值的欄位仍保留，值顯示「-」 */
const getReporterInfos = (report) =>
	getReporterFields(report).map(({ key, label }) => ({ label, value: report.reporterInfo?.[key] || "-" }));

const getDefaultSearchParams = () => ({
	errorTypes: [],
	year: null,
	edu: null,
	subject: null,
	availabilities: null,
	comeFrom: null,
	status: null,
	CMS: "",
	description: "",
	startDate: moment().subtract(1, "month").format(DATE_FORMAT),
	endDate: moment().format(DATE_FORMAT),
});

const downloadBlob = (blob, fileName) => {
	const blobUrl = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = blobUrl;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(blobUrl);
};

/** 下架產品後端支援複選，畫面目前是單選，送出時包成陣列 */
const toQueryPayload = (params, page, pageSize) => ({
	pagenumber: page,
	pagesize: pageSize,
	...params,
	availabilities: params.availabilities ? [params.availabilities] : null,
});

export const ErrorReportPage = () => {
	const [selection, setSelection] = useState({});
	const [searchParams, setSearchParams] = useState(getDefaultSearchParams);
	const [reports, setReports] = useState([]);
	const [total, setTotal] = useState(0);
	const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE });
	const [isLoading, setIsLoading] = useState(false);
	const [checkList, setCheckList] = useState([]);
	const [previewReport, setPreviewReport] = useState(null);
	const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeprecateModalOpen, setIsDeprecateModalOpen] = useState(false);
	const [isDeprecating, setIsDeprecating] = useState(false);
	const [mailReport, setMailReport] = useState(null);
	const [isSendingMail, setIsSendingMail] = useState(false);
	const [remarkReports, setRemarkReports] = useState(null);
	const [isUpdatingRemark, setIsUpdatingRemark] = useState(false);
	const [isExporting, setIsExporting] = useState(false);

	const { errorTypeMap, yearMap, eduMap, eduSubjectMap, availabilityMap, comeFromMap, statusMap } = selection;

	const subjectOptions = useMemo(() => {
		if (!searchParams.edu || !eduSubjectMap) return [];
		return setSelectOptions(eduSubjectMap[searchParams.edu]);
	}, [searchParams.edu, eduSubjectMap]);

	const changeDateRangeHandler = (_, [startDate, endDate]) => {
		setSearchParams((prev) => ({ ...prev, startDate, endDate }));
	};

	const changeSearchParams = (key, value) => {
		setSearchParams((prev) => ({
			...prev,
			[key]: value,
			// 學制變更時，科目需一併清空
			...(key === "edu" ? { subject: null } : {}),
		}));
	};

	const fetchSelection = async () => {
		try {
			const { error, data, message } = await getErrorReportSelection();
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return null;
			}
			setSelection(data || {});
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		}
	};

	const fetchReports = async (params, nextPage, pageSize) => {
		try {
			setIsLoading(true);
			const { error, data, message } = await getErrorReportList(toQueryPayload(params, nextPage, pageSize));
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return;
			}
			setReports(data?.reports || []);
			setTotal(data?.total || 0);
			setCheckList([]);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		// 篩選條件皆為選填，初次進頁不帶條件，直接查全部
		fetchSelection();
		fetchReports(searchParams, 1, pagination.pageSize);
	}, []);

	const searchHandler = () => {
		setPagination((prev) => ({ ...prev, page: 1 }));
		fetchReports(searchParams, 1, pagination.pageSize);
	};

	const pageChangeHandler = (nextPage, pageSize) => {
		setPagination({ page: nextPage, pageSize });
		fetchReports(searchParams, nextPage, pageSize);
	};

	const isChecked = (report) => checkList.some(({ UID }) => UID === report.UID);
	const isCheckAll = reports.length > 0 && checkList.length === reports.length;
	const indeterminate = !isCheckAll && checkList.length > 0;
	const hasNoCheckedItem = checkList.length === 0;

	const checkHandler = (event, report) => {
		if (event.target.checked) {
			setCheckList((prev) => (prev.some(({ UID }) => UID === report.UID) ? prev : [...prev, report]));
		} else {
			setCheckList((prev) => prev.filter(({ UID }) => UID !== report.UID));
		}
	};

	const checkAllHandler = (event) => {
		setCheckList(event.target.checked ? [...reports] : []);
	};

	const updateStatusHandler = async (status) => {
		try {
			setIsUpdating(true);
			const { error, message } = await updateErrorReportsStatus({
				UIDs: checkList.map(({ UID }) => UID),
				status,
			});
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return;
			}
			openNotificationWithIcon("success", message);
			setIsStatusModalOpen(false);
			fetchReports(searchParams, pagination.page, pagination.pageSize);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsUpdating(false);
		}
	};

	const deprecateHandler = async (availabilities) => {
		try {
			setIsDeprecating(true);
			const { error, message } = await deprecateErrorReportQuestions({
				UIDs: checkList.map(({ UID }) => UID),
				availabilities,
			});
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return;
			}
			openNotificationWithIcon("success", message);
			setIsDeprecateModalOpen(false);
			fetchReports(searchParams, pagination.page, pagination.pageSize);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsDeprecating(false);
		}
	};

	const sendMailHandler = async () => {
		try {
			setIsSendingMail(true);
			const { error, message } = await sendErrorReportMails({ UIDs: [mailReport.UID] });
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return;
			}
			openNotificationWithIcon("success", message);
			setMailReport(null);
			fetchReports(searchParams, pagination.page, pagination.pageSize);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsSendingMail(false);
		}
	};

	const updateRemarkHandler = async (remark) => {
		try {
			setIsUpdatingRemark(true);
			const { error, message } = await updateErrorReportsRemark({
				UIDs: remarkReports.map(({ UID }) => UID),
				remark,
			});
			if (error !== API_SUCCESS_CODE) {
				openNotificationWithIcon("error", message);
				return;
			}
			openNotificationWithIcon("success", message);
			setRemarkReports(null);
			fetchReports(searchParams, pagination.page, pagination.pageSize);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsUpdatingRemark(false);
		}
	};

	const exportHandler = async () => {
		try {
			setIsExporting(true);
			const blob = await exportErrorReports({ UIDs: checkList.map(({ UID }) => UID) });
			// 失敗時後端回傳 ResponseDto JSON 而非檔案
			if (blob.type.includes("json")) {
				const { message } = JSON.parse(await blob.text());
				openNotificationWithIcon("error", message);
				return;
			}

			downloadBlob(blob, `錯題回報列表_${moment().format("YYYYMMDD")}.xlsx`);
		} catch (error) {
			openNotificationWithIcon("error", error?.message || error);
		} finally {
			setIsExporting(false);
		}
	};

	return (
		<StyledErrorReportPage>
			<div className="breadBlock">
				<Breadcrumb data={breadcrumbList} />
			</div>
			<div className="searchToolBar">
				<div className="searchRow">
					<div className="errorTypeFilter">
						<PickListDropdown
							defaultTitle="回報原因"
							options={setSelectOptions(errorTypeMap)}
							value={searchParams.errorTypes}
							onChange={(val) => changeSearchParams("errorTypes", val)}
						/>
					</div>
					<Select
						className="w120"
						placeholder="學年度"
						options={setSelectOptions(yearMap)}
						value={searchParams.year}
						onChange={(val) => changeSearchParams("year", val ?? null)}
					/>
					<Select
						className="w70"
						placeholder="學制"
						options={setSelectOptions(eduMap)}
						value={searchParams.edu}
						onChange={(val) => changeSearchParams("edu", val ?? null)}
					/>
					<Select
						placeholder="科目"
						options={subjectOptions}
						value={searchParams.subject}
						onChange={(val) => changeSearchParams("subject", val ?? null)}
					/>
					<Select
						className="w100"
						placeholder="下架產品"
						options={setSelectOptions(availabilityMap)}
						value={searchParams.availabilities}
						onChange={(val) => changeSearchParams("availabilities", val ?? null)}
					/>
					<Select
						className="w100"
						placeholder="回報來源"
						options={setSelectOptions(comeFromMap)}
						value={searchParams.comeFrom}
						onChange={(val) => changeSearchParams("comeFrom", val ?? null)}
					/>
					<Select
						className="w120"
						placeholder="狀態"
						options={setSelectOptions(statusMap)}
						value={searchParams.status}
						onChange={(val) => changeSearchParams("status", val ?? null)}
					/>
				</div>

				<div className="searchRow">
					<div className="dateFilter">
						<RangePicker
							value={[moment(searchParams.startDate, DATE_FORMAT), moment(searchParams.endDate, DATE_FORMAT)]}
							format={DATE_FORMAT}
							allowClear={false}
							onChange={changeDateRangeHandler}
						/>
					</div>
					<div className="cmsInput">
						<Input
							placeholder="CMS編號"
							value={searchParams.CMS}
							onChange={(e) => changeSearchParams("CMS", e.target.value)}
							onKeyEnter={searchHandler}
						/>
					</div>
					<div className="descriptionInput">
						<Input
							placeholder="請輸入回報內容"
							value={searchParams.description}
							onChange={(e) => changeSearchParams("description", e.target.value)}
							onKeyEnter={searchHandler}
						/>
					</div>
					<div className="searchBtn">
						<Button variant="orange" loading={isLoading} onClick={searchHandler}>
							搜 尋
						</Button>
					</div>
				</div>
			</div>
			<div className="countText">
				篩選結果 <span>{reports.length}</span>
				<span className="totalCount">/ {total}</span>
			</div>
			<Table margin="3" className="errorReportTable">
				<Table.Header className="header">
					<Table.Row>
						<Table.Item className="checkboxItem" flex="0.1">
							<Checkbox indeterminate={indeterminate} checked={isCheckAll} onChange={checkAllHandler} />
						</Table.Item>
						<Table.Item flex="0.5">CMS編號</Table.Item>
						<Table.Item className="previewItem" flex="0.6">
							圖片預覽
						</Table.Item>
						<Table.Item className="actionItem" flex="0.8"></Table.Item>
						<Table.Item className="comeFromItem" flex="0.8">
							回報來源
						</Table.Item>
						<Table.Item flex="1.2">使用者ID</Table.Item>
						<Table.Item flex="1">錯誤訊息</Table.Item>
						<Table.Item flex="1.6">回報者留言</Table.Item>
						<Table.Item flex="1.6">備註留言</Table.Item>
						<Table.Item flex="1.2">回報時間</Table.Item>
						<Table.Item className="productItem" flex="0.6">
							下架
						</Table.Item>
						<Table.Item flex="0.7">狀態</Table.Item>
						<Table.Item className="mailItem" flex="0.4">
							寄信
						</Table.Item>
					</Table.Row>
				</Table.Header>
				<Table.Body className="body">
					{isLoading ? (
						<div className="loading">
							<Spin indicator={<Loading3QuartersOutlined spin={true} />} size="large" />
						</div>
					) : reports.length > 0 ? (
						reports.map((report) => (
							<Table.Row key={report.UID}>
								<Table.Item className="checkboxItem" flex="0.1">
									<Checkbox checked={isChecked(report)} onChange={(event) => checkHandler(event, report)} />
								</Table.Item>
								{/* CMS編號 */}
								<Table.Item flex="0.5">{report.CMS}</Table.Item>
								{/* 圖片預覽 */}
								<Table.Item className="previewItem" flex="0.6">
									<div className="previewImage">
										{report.questionImage ? (
											<Image
												src={report.questionImage}
												preview={{ getContainer: document.getElementById("errorReportPreview") }}
											/>
										) : (
											<Icon name="image" type="outLined" />
										)}
									</div>
								</Table.Item>
								<Table.Item className="actionItem" flex="0.8">
									<Button.IconButton
										variant="orange"
										full={false}
										iconName="visibility"
										onClick={() => setPreviewReport(report)}>
										看更多
									</Button.IconButton>
								</Table.Item>
								{/* 回報來源 */}
								<Table.Item className="comeFromItem" flex="0.8">
									{report.comeFrom?.name || "-"}
								</Table.Item>
								{/* 使用者ID */}
								<Table.Item className="reporterInfoItem" flex="1.2">
									{getReporterInfos(report).length > 0
										? getReporterInfos(report).map(({ label, value }) => (
												<div key={label}>
													{label}：{value}
												</div>
										  ))
										: "-"}
								</Table.Item>
								{/* 錯誤訊息 */}
								<Table.Item className="errorTypeItem" flex="1">
									{(report.errorTypes || []).map(({ code, name }) => (
										<div key={code}>{name}</div>
									))}
								</Table.Item>
								{/* 回報者留言 */}
								<Table.Item className="descriptionItem" flex="1.6">
									{report.description ? report.description : "-"}
								</Table.Item>
								{/* 備註留言 */}
								<Table.Item className="remarkItem" flex="1.6">
									<div className="remarkText">{report.remark || "-"}</div>
									<Icon name="edit" color="#82b4ff" clickable={true} onClick={() => setRemarkReports([report])} />
								</Table.Item>
								{/* 回報時間 */}
								<Table.Item className="timeItem" flex="1.2">
									{/* NOTE: createTime 後端已加 8 小時轉為台北時間，但仍以 Z 結尾，需用 utc 解析避免再被加一次時差 */}
									<div>{moment.utc(report.createTime).format("YYYY-MM-DD")}</div>
									<div>{moment.utc(report.createTime).format("HH:mm")}</div>
								</Table.Item>
								{/* 下架 */}
								<Table.Item className="productItem" flex="0.6">
									{(report.availabilities || []).map(({ code, name, isAvailable }) => (
										<span key={code} className={`tag ${isAvailable ? "online" : "offline"}`}>
											{name}
										</span>
									))}
								</Table.Item>
								{/* 狀態 */}
								<Table.Item flex="0.7">
									<span className={`statusTag ${report.status?.code}`}>{report.status?.name}</span>
								</Table.Item>
								{/* 寄信 */}
								<Table.Item className="mailItem" flex="0.4">
									<Icon
										name="mail_outline"
										type="outLined"
										color={MAIL_STATUS_COLOR[getMailStatus(report)]}
										clickable={getMailStatus(report) === MAIL_STATUS.SENDABLE}
										disabled={getMailStatus(report) === MAIL_STATUS.UNAVAILABLE}
										onClick={() => getMailStatus(report) === MAIL_STATUS.SENDABLE && setMailReport(report)}
									/>
								</Table.Item>
							</Table.Row>
						))
					) : (
						<div className="tableNoData">
							<img src={`/assets/noData.png`} alt="noData" />
							無資料顯示
						</div>
					)}
				</Table.Body>
			</Table>
			<Pagination
				total={total}
				defaultCurrent={1}
				current={pagination.page}
				onChange={pageChangeHandler}
				defaultPageSize={PAGE_SIZE}
			/>
			<div className="buttonGroup">
				<div className="btn">
					<Button.IconButton
						variant="green"
						full={false}
						iconName="save_alt"
						disabled={hasNoCheckedItem}
						loading={isExporting}
						onClick={exportHandler}>
						下載報表
					</Button.IconButton>
				</div>
				<div className="btn">
					<Button.IconButton
						variant="red"
						full={false}
						iconName="close"
						disabled={hasNoCheckedItem}
						onClick={() => setIsDeprecateModalOpen(true)}>
						題目下架
					</Button.IconButton>
				</div>
				<div className="btn">
					<Button.IconButton
						variant="blue"
						full={false}
						iconName="edit"
						disabled={hasNoCheckedItem}
						onClick={() => setIsStatusModalOpen(true)}>
						修改狀態
					</Button.IconButton>
				</div>
				<div className="btn">
					<Button.IconButton
						variant="orange"
						full={false}
						iconName="edit"
						disabled={hasNoCheckedItem}
						onClick={() => setRemarkReports(checkList)}>
						批次備註
					</Button.IconButton>
				</div>
			</div>
			<UiPopupContainer id="errorReportPreview" />
			{previewReport && <QuestionPreviewModal report={previewReport} onCancel={() => setPreviewReport(null)} />}
			{isStatusModalOpen && (
				<UpdateStatusModal
					count={checkList.length}
					statusOptions={setSelectOptions(statusMap)}
					isLoading={isUpdating}
					onOk={updateStatusHandler}
					onCancel={() => setIsStatusModalOpen(false)}
				/>
			)}
			{isDeprecateModalOpen && (
				<DeprecateQuestionModal
					isLoading={isDeprecating}
					onOk={deprecateHandler}
					onCancel={() => setIsDeprecateModalOpen(false)}
				/>
			)}
			{mailReport && (
				<SendMailModal
					report={mailReport}
					isLoading={isSendingMail}
					onOk={sendMailHandler}
					onCancel={() => setMailReport(null)}
				/>
			)}
			{remarkReports && (
				<UpdateRemarkModal
					reports={remarkReports}
					isLoading={isUpdatingRemark}
					onOk={updateRemarkHandler}
					onCancel={() => setRemarkReports(null)}
				/>
			)}
		</StyledErrorReportPage>
	);
};

export default ErrorReportPage;
